import React, { useCallback, useEffect, useMemo, useState } from "react";
import cookies from "../../../../../../CommonFunction/cookies";
import { usePagination, useTable } from "react-table";
import * as XLSX from "xlsx";
import Popup from "reactjs-popup";
import { DashboardLoading } from "../../../Elements/ServeLoading";
import EmptyDataAlert from "../../../Elements/EmptyDataAlert";
import DataTable from "../../../Elements/DataTable";
import { Pagination2 } from "../../../Elements/Pagination";
import { NoticeDeliveryDefault } from "../../../../../../API/Bank/Dashboard";
import { IoEyeSharp, IoInformationCircleOutline } from "react-icons/io5";
import { formatDate } from "../../../../../../CommonFunction/FormatedDate";
import { FaDownload, FaSpinner } from "react-icons/fa";
import { Tooltip } from "react-tooltip";

const DeliveryDefault = ({ isOpen, onClose, bankID, branchID, role }) => {
  const jwtToken = cookies.get("jwt");
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [isExport, setIsExport] = useState(true);
  const [isExportLoading, setExportLoading] = useState();
  const [reportDetail, setReportDetails] = useState([]);
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const columns = useMemo(
    () => [
      {
        Header: "Loan Number",
        accessor: "loan_id",
        Cell: ({ row, value }) => (
          <div className="my-1 px-2">
            <p>{value}</p>
          </div>
        ),
      },
      {
        Header: "CUID",
        accessor: "system_id",
        Cell: ({ row }) => {
          const UCID = row?.original?.user_or_org?.system_id;
          const borrowerName = row?.original?.user_or_org;
          return (
            <div className="flex items-center justify-center gap-2 text-center">
              {UCID}
              <button data-tooltip-id={`tooltip-${UCID}`}>
                <span className="text-red-600">
                  <IoInformationCircleOutline size={24} />
                </span>
              </button>
              <Tooltip id={`tooltip-${UCID}`}>
                Name:{" "}
                <b>
                  {" "}
                  {borrowerName?.first_name || ""}{" "}
                  {borrowerName?.middle_name || ""}{" "}
                  {borrowerName?.last_name || ""} {borrowerName?.name || ""}
                </b>
              </Tooltip>
            </div>
          );
        },
      },
      {
        Header: "Notice Type",
        accessor: "type",
        Cell: ({ row }) => <div>{row?.original?.notices[0]?.type || ""}</div>,
      },
      {
        Header: "Envelope ID",
        accessor: "envelope_id",
      },
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ row }) => (
          <div>{row?.original?.notices[0]?.category || ""}</div>
        ),
      },
      {
        Header: "Details",
        accessor: "status",
        Cell: ({ row }) => (
          <button
            onClick={() => openReport(row?.original?.notices)}
            className="text-center border-bgclr2"
          >
            <IoEyeSharp className="text-bgclr" size={22} />
          </button>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns,
        data: loanData,
        initialState: { pageIndex: 0 },
        manualPagination: true,
      },
      usePagination
    );

  const openReport = (data) => {
    console.log("Report of this notice = ", data);
    setReportDetails(data);
  };

  const closeReport = () => {
    setReportDetails([]);
  };

  const fetchData = useCallback(
    async (page = 1, query = searchQuery) => {
      setLoading(true);
      try {
        const data = await NoticeDeliveryDefault(
          !isExport,
          jwtToken,
          page,
          query,
          bankID,
          branchID,
          role
        );
        if (data?.docs) {
          setLoanData(data.docs);
          setPagination({
            totalDocs: data.totalDocs,
            totalPages: data.totalPages,
            page: data.page,
            limit: data.limit,
            hasNextPage: data.hasNextPage,
            hasPrevPage: data.hasPrevPage,
          });
          setError(false);
        } else {
          setLoanData([]);
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [jwtToken]
  );

  const exportToExcel = async () => {
    setExportLoading(true);
    const loansDetails = await NoticeDeliveryDefault(
      isExport,
      jwtToken,
      "",
      "",
      bankID,
      branchID,
      role
    );
    setExportLoading(false);

    const formatedData = [
      ...loansDetails?.docs?.map((user) => ({
        Name: `${user?.user_or_org?.first_name || ""} ${
          user?.user_or_org?.middle_name || ""
        } ${user?.user_or_org?.last_name || ""} ${
          user?.user_or_org?.name || ""
        }`.trim(),
        Loan: user?.loan_id,
        CUID: user?.user_or_org?.system_id,
        Email: user?.user_or_org?.email,
        Phone: user?.user_or_org?.phone_number,
        Role: user?.notices[0]?.category,
      })),
    ];

    const ws = XLSX.utils.json_to_sheet(formatedData);

    // Set column widths
    const columnWidths = [
      { wch: 20 },
      { wch: 25 },
      { wch: 20 },
      { wch: 30 },
      { wch: 25 },
      { wch: 10 },
      { wch: 20 },
    ];
    ws["!cols"] = columnWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DeliveryDefault");
    XLSX.writeFile(wb, "Delivery_Default_Report.xlsx");
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = async () => {
    setLoading(true);
    fetchData(1, searchQuery);
  };

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) fetchData(pagination.page + 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  const previousPage = useCallback(() => {
    if (pagination.hasPrevPage) fetchData(pagination.page - 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  return (
    <>
      <Popup
        open={isOpen}
        modal
        closeOnDocumentClick={false}
        className="custom-popup"
      >
        <div className="popup-style popup-content">
          <div className="bg-bgclr8 md:w-[950px] w-[400px]  mx-auto h-[520px] rounded-[14px]">
            <div className="flex justify-between font-avenir text-bgclr px-10 pt-5">
              <h1 className="text-[20px] font-[600] ">Delivery Defaults</h1>
              <div className="flex gap-4">
                <button
                  type="submit"
                  onClick={() => {
                    exportToExcel();
                  }}
                  disabled={loading || isExportLoading}
                  className="flex gap-2 items-center py-1 px-7  bg-bgclr rounded-md text-txtclr"
                >
                  {isExportLoading ? (
                    <FaSpinner size={18} className="animate-spin text-txtclr" />
                  ) : (
                    <FaDownload size={18} />
                  )}{" "}
                  Download Report
                </button>
                <button
                  type="submit"
                  onClick={() => {
                    onClose();
                  }}
                  // disabled={loading || isExportLoading}
                  className="font-bold py-1 px-3 rounded-md bg-red-700 text-txtclr"
                >
                  X
                </button>
              </div>
            </div>
            <div>
              {!showLoanDetails ? (
                <div>
                  <div className="searchBar-style-dashboard">
                    <input
                      type="search"
                      placeholder="Search by loan number & envelope id"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="searchBar-input-dashboard"
                    />
                    <button
                      onClick={() => handleSearch()}
                      className="searchBar-btn-dashboard"
                    >
                      Search
                    </button>
                  </div>
                  {loading ? (
                    <div className="flex justify-center h-[360px] items-center">
                      <DashboardLoading />
                    </div>
                  ) : (
                    <>
                      {loanData.length === 0 ? (
                        <div className="h-screen">
                          <EmptyDataAlert
                            title={
                              error
                                ? "No Loans Found"
                                : "Search Loan by Loan number"
                            }
                          />
                        </div>
                      ) : (
                        <div className="mt-2">
                          <div>
                            <div className="pt-3 w-[900px] mx-auto overflow-x-auto h-[335px] overflow-y-auto ">
                              <DataTable
                                getTableProps={getTableProps}
                                headerGroups={headerGroups}
                                getTableBodyProps={getTableBodyProps}
                                page={page}
                                prepareRow={prepareRow}
                                selectedLoanId={""}
                              />
                            </div>
                            <Pagination2
                              previousPage={previousPage}
                              nextPage={nextPage}
                              canPreviousPage={pagination.hasPrevPage}
                              canNextPage={pagination.hasNextPage}
                              pagination={{
                                page: pagination.page,
                                totalPages: pagination.totalPages,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>

        {reportDetail?.length > 0 &&
          (() => {
            const whatsapp =
              reportDetail[1]?.responses[
                reportDetail[1]?.responses?.length - 1
              ];
            const email =
              reportDetail[3].responses[reportDetail[3].responses.length - 1];
            const ivr =
              reportDetail[2]?.responses[
                reportDetail[2]?.responses?.length - 1
              ];
            const sms =
              reportDetail[0]?.responses[
                reportDetail[0]?.responses?.length - 1
              ];
            console.log("Console what data --", ivr);
            return (
              <div className="popup-style popup-content">
                <div className="bg-bgclr8 md:w-[950px] w-[400px]  mx-auto overflow-x-auto px-10 py-5 rounded-[14px]">
                  <div className="flex justify-between items-center">
                    <h1 className="text-[18px] font-[600]">
                      Dispatch Date & Time
                    </h1>
                    <button
                      type="submit"
                      onClick={() => {
                        closeReport();
                      }}
                      // disabled={loading}
                      className="text-xl font-bold"
                    >
                      X
                    </button>
                  </div>
                  <div className="border mt-3 rounded-lg">
                    <div className="flex">
                      <div className="w-[50%] border-r px-7 py-7">
                        <h2 className="text-[16px] font-[600]">EMAIL</h2>
                        <table className="text-[14px] leading-6">
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Delivery Address:{" "}
                            </td>
                            <td>{formatDate(reportDetail[3]?.created_at)}</td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">Status: </td>
                            <td
                              className={`font-[500] ${
                                email?.event === "bounce" || !email
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {email?.event === "bounce" || !email
                                ? "Failed"
                                : "Success"}
                            </td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Last Status Time:{" "}
                            </td>
                            <td>
                              {formatDate(
                                email?.openTime ||
                                  email?.deliveryTime ||
                                  email?.sendTime ||
                                  reportDetail[3]?.created_at
                              )}
                            </td>
                          </tr>
                        </table>
                      </div>

                      <div className="w-[50%] px-7 py-7">
                        <h2 className="text-[16px] font-[600]">SMS</h2>
                        <table className="text-[14px] leading-6">
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Delivery Address:{" "}
                            </td>
                            <td>{formatDate(reportDetail[0]?.created_at)}</td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">Status: </td>
                            <td
                              className={`font-[500] ${
                                sms?.status === "failed"
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {sms?.status === "failed" ? "Failed" : "Success"}
                            </td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Last Status Time:{" "}
                            </td>
                            <td>{formatDate(sms?.dtime || sms?.ts)}</td>
                          </tr>
                        </table>
                      </div>
                    </div>

                    <hr className="h-px w-[830px] mx-auto bg-gray-200 border-0 " />

                    <div className="flex">
                      <div className="w-[50%] border-r px-7 py-7">
                        <h2 className="text-[16px] font-[600]">WhatsApp</h2>
                        <table className="text-[14px] leading-6">
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Delivery Address:{" "}
                            </td>
                            <td>{formatDate(reportDetail[1]?.created_at)}</td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">Status: </td>
                            <td
                              className={`font-[500] ${
                                whatsapp?.status === "failed"
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {whatsapp?.status === "failed"
                                ? "Failed"
                                : "Success"}
                            </td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Last Status Time:{" "}
                            </td>
                            <td>
                              {formatDate(
                                whatsapp?.time || reportDetail[1]?.created_at
                              )}
                            </td>
                          </tr>
                        </table>
                      </div>

                      <div className="w-[50%] px-7 py-7">
                        <h2 className="text-[16px] font-[600]">IVR</h2>
                        <table className="text-[14px] leading-6">
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Delivery Address:{" "}
                            </td>
                            <td>{formatDate(reportDetail[2]?.created_at)}</td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">Status: </td>
                            <td
                              className={`font-[500] ${
                                ivr?.status === "failed" ||
                                !ivr.customer_keypress
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {ivr?.status === "failed" ||
                              !ivr.customer_keypress
                                ? "Failed"
                                : "Success"}
                            </td>
                          </tr>
                          <tr>
                            <td className="w-[190px] opacity-60">
                              Last Status Time:{" "}
                            </td>
                            <td>
                              {formatDate(
                                ivr?.start_time || reportDetail[2]?.created_at
                              )}
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
      </Popup>
    </>
  );
};

export default DeliveryDefault;
